import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

/**
 * Persona por região (spec do handoff): troca nome/iniciais/cidade nos mocks
 * do hero e do Card 360 conforme o idioma do navegador, com override manual
 * via `?persona=<região>` para QA.
 */
export interface Persona {
  name: string
  init: string
  city: string
}

export const PERSONAS: Record<string, Persona> = {
  br: { name: 'Rafa Lima', init: 'RL', city: 'Rio de Janeiro' },
  us: { name: 'Tyler Brooks', init: 'TB', city: 'Austin' },
  es: { name: 'Mateo Ríos', init: 'MR', city: 'Buenos Aires' },
  jp: { name: 'Kenji Sato', init: 'KS', city: 'Tóquio' },
  fr: { name: 'Amara Diallo', init: 'AD', city: 'Paris' },
  ae: { name: 'Khalid Mansour', init: 'KM', city: 'Abu Dhabi' },
  en: { name: 'Jordan Reid', init: 'JR', city: 'Londres' },
}

export function regionFromLanguage(lang: string): string {
  const l = lang.toLowerCase()
  if (l.startsWith('pt')) return 'br'
  if (l === 'en-us') return 'us'
  if (l.startsWith('es')) return 'es'
  if (l.startsWith('ja')) return 'jp'
  if (l.startsWith('fr')) return 'fr'
  if (l.startsWith('ar')) return 'ae'
  return 'en'
}

export function usePersona(): Persona {
  const router = useRouter()
  // SSR renderiza a persona brasileira (site é pt-BR); o efeito ajusta no
  // cliente pelo idioma do navegador.
  const [persona, setPersona] = useState<Persona>(PERSONAS.br)

  useEffect(() => {
    if (!router.isReady) return
    const override = String(router.query.persona || '')
    if (override && PERSONAS[override]) {
      setPersona(PERSONAS[override])
      return
    }
    const lang =
      (typeof navigator !== 'undefined' && navigator.language) || 'pt-BR'
    setPersona(PERSONAS[regionFromLanguage(lang)] || PERSONAS.br)
  }, [router.isReady, router.query.persona])

  return persona
}

/**
 * Heatmap de treinos: 126 células determinísticas (PRNG semeado, seed 7),
 * distribuição ~30% n0 / 22% n1 / 22% n2 / 16% n3 / 10% n4 — igual no server
 * e no cliente, sem mismatch de hidratação.
 */
export function heatLevels(): number[] {
  const levels: number[] = []
  let seed = 7
  const rnd = () => {
    seed = (seed * 16807) % 2147483647
    return seed / 2147483647
  }
  for (let i = 0; i < 126; i++) {
    const r = rnd()
    levels.push(r < 0.3 ? 0 : r < 0.52 ? 1 : r < 0.74 ? 2 : r < 0.9 ? 3 : 4)
  }
  return levels
}
